import { FC } from "react";
import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import DefaultContactForm, {
  ContactFormData,
  EditContactFormData,
} from "./DefaultContactForm";
import { updateContact } from "@/api";
import { useSnackbar, VariantType } from "notistack";
import { usePermissions } from "@/hooks/usePermissions";
import LoggingAPI from "@/api/logging";
import { Contact } from "../contacts.types";
import { useCustomFields } from "../hooks/useCustomFields";
import {
  processEditCustomAttributesForAPI,
  extractCustomFieldsFromFormData,
} from "./utils/customAttributeUtils";

interface EditContactFormProps {
  width?: number | undefined;
  defaultFormData: Contact;
  onSubmit: () => void;
  onCancel: () => void;
}

const EditContactForm: FC<EditContactFormProps> = ({
  width,
  defaultFormData,
  onSubmit,
  onCancel,
}) => {
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const { data: customFields = [] } = useCustomFields();

  const handleSubmit = async (
    data: ContactFormData,
    diffData?: EditContactFormData
  ) => {
    try {
      // Extract custom field data from diff data
      const cleanedDiffData = extractCustomFieldsFromFormData(diffData || {});

      // Process custom attributes for editing (only changed attributes)
      const customAttributes = processEditCustomAttributesForAPI(
        diffData || {},
        customFields,
        defaultFormData.client_id,
        defaultFormData.custom_attributes || []
      );

      // Include custom attributes in the update if there are any
      const updateData = {
        ...cleanedDiffData,
        ...(customAttributes.length > 0 && {
          custom_attributes: customAttributes,
        }),
      };

      await updateContact(defaultFormData.client_id, updateData);
      onSubmit?.();
      enqueueSnackbar(`Contact ${data?.name || ""} Edited`, {
        variant: "success" as VariantType,
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Edited Contact: ${data?.name}`,
        event: "contact:update",
        object_type: "contact",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        `An error occurred while editing contact ${
          data?.name || ""
        }. Please try again.`,
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <FormTitle>Edit Contact</FormTitle>
      <DefaultContactForm
        onSubmit={handleSubmit}
        onCancel={onCancel}
        defaultFormData={defaultFormData}
        buttonProps={{ submitText: "Edit Contact" }}
      />
    </FormWrapper>
  );
};
export default EditContactForm;
